/**
 * GlobalMenu.jsx - Shared Navigation Menu Component
 * 
 * STANDARDIZATION MANDATE (2026-04-25):
 * - Tajweed Coach positioned BELOW Masjid Guide in all themes
 * - Feature parity across all themes
 * - Reusable component with theme and moduleType props
 * 
 * ORDER (Non-Kids themes):
 * 1. Home
 * 2. Quran
 * 3. Prayer Times
 * 4. Duas
 * 5. Hadith
 * 6. Masjid Guide (PRO)
 * 7. Masjid Connect (PRO)
 * 8. Tajweed Coach (PRO) - NOW BELOW MASJID GUIDE
 * 9. Halal Scanner (PRO)
 * 10. Progress (PRO)
 * 11. Zoom (PRO)
 * 12. Qibla
 * 13. Saved
 * 14. Search
 * 15. Settings
 */

import React, { useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  Home, BookOpen, Clock, HandHeart, BookText, MapPin, Radio, 
  GraduationCap, Scan, TrendingUp, ZoomIn, Compass, Bookmark, 
  Search, Settings, Crown, Sparkles
} from 'lucide-react';
import { usePro } from '../../contexts/ProContext';
import { useTheme } from '../../contexts/ThemeContext';
import { Tooltip, TooltipTrigger, TooltipContent } from '../ui/tooltip';

/**
 * Get standardized navigation items with proper ordering
 * Tajweed Coach is positioned BELOW Masjid Guide/Connect
 * 
 * @param {Function} t - Translation function
 * @param {string} appTheme - Current theme (classic, emerald, royal, kids)
 * @returns {Array} Navigation items in standardized order
 */
export const getGlobalNavItems = (t, appTheme) => {
  const baseItems = [
    { path: '/', label: t('nav.home', 'Home'), icon: Home, id: 'home' },
    { path: '/quran', label: t('nav.quran', 'Quran'), icon: BookOpen, id: 'quran' },
    { path: '/prayer-times', label: t('nav.prayerTimes', 'Prayer Times'), icon: Clock, id: 'prayer' },
    { path: '/duas', label: t('nav.duas', 'Duas'), icon: HandHeart, id: 'duas' },
  ];

  // Kids theme: Show Learn instead of Hadith, AND include Tajweed Coach
  if (appTheme === 'kids') {
    baseItems.push({ 
      path: '/learn', 
      label: t('nav.learn', 'Learn'), 
      icon: GraduationCap, 
      id: 'learn' 
    });
    // ADDED: Tajweed Coach for Kids Paradise (2026-04-25)
    baseItems.push({ 
      path: '/quran?tajweed=true', 
      label: t('nav.tajweedCoach', 'Tajweed Coach'), 
      icon: GraduationCap, 
      isPro: true, 
      featureId: 'tajweed_coach',
      id: 'tajweed-coach'
    });
  } else {
    baseItems.push({ 
      path: '/hadith', 
      label: t('nav.hadith', 'Hadith'), 
      icon: BookText, 
      id: 'hadith' 
    });
  }

  // Non-Kids themes: Add Masjid Guide, Masjid Connect, THEN Tajweed Coach
  if (appTheme !== 'kids') {
    baseItems.push(
      { 
        path: '/mosques', 
        label: t('nav.masjidGuide', 'Masjid Guide'), 
        icon: MapPin, 
        isPro: true, 
        featureId: 'mosque_finder',
        id: 'masjid-guide'
      },
      { 
        path: '/prayer-times#masjid-connect', 
        label: t('nav.masjidConnect', 'Masjid Connect'), 
        icon: Radio, 
        isPro: true, 
        featureId: 'masjid_connect',
        id: 'masjid-connect'
      },
      // CRITICAL: Tajweed Coach is NOW positioned AFTER Masjid Guide/Connect
      { 
        path: '/quran?tajweed=true', 
        label: t('nav.tajweedCoach', 'Tajweed Coach'), 
        icon: GraduationCap, 
        isPro: true, 
        featureId: 'tajweed_coach',
        id: 'tajweed-coach'
      }
    );
  }

  // Common remaining items (all themes)
  baseItems.push(
    { 
      path: '/halal-scanner', 
      label: t('nav.halalScanner', 'Halal Scanner'), 
      icon: Scan, 
      isPro: true, 
      featureId: 'halal_scanner',
      isHighlight: true,
      id: 'halal-scanner'
    },
    { 
      path: '/progress', 
      label: t('nav.progress', 'Progress'), 
      icon: TrendingUp, 
      isPro: true, 
      featureId: 'reading_stats',
      id: 'progress'
    },
    { 
      path: '/quran/zoom/1', 
      label: t('nav.zoom', 'Zoom'), 
      icon: ZoomIn, 
      isPro: true, 
      featureId: 'zoom_mode',
      isZoom: true,
      id: 'zoom'
    },
    { path: '/qibla', label: t('nav.qibla', 'Qibla'), icon: Compass, id: 'qibla' },
    { path: '/bookmarks', label: t('nav.saved', 'Saved'), icon: Bookmark, id: 'saved' },
    // Search hidden as per user request
    { path: '/settings', label: t('nav.settings', 'Settings'), icon: Settings, id: 'settings' }
  );

  return baseItems;
};

/**
 * GlobalMenu Component
 * Renders navigation menu with consistent ordering across all themes
 */
export const GlobalMenu = ({ 
  variant = 'sidebar', // 'sidebar' | 'dock' | 'mobile'
  className = '',
  onItemClick = null,
  showLabels = true
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { appTheme } = useTheme();
  const { isPro, requestFeatureAccess, isFeatureUnlockedViaAd } = usePro();

  const navItems = useMemo(() => getGlobalNavItems(t, appTheme), [t, appTheme]);

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    if (path === '/quran') return location.pathname === '/quran' || location.pathname.match(/^\/quran\/\d+$/);
    if (path.includes('/zoom/')) return location.pathname.includes('/zoom');
    if (path.includes('?tajweed=true')) return location.search.includes('tajweed=true');
    return location.pathname.startsWith(path.split('#')[0].split('?')[0]);
  };

  const handleNavClick = (e, item) => {
    const isItemLocked = item.isPro && !isPro && !(item.featureId && isFeatureUnlockedViaAd(item.featureId));
    
    if (isItemLocked) {
      e.preventDefault();
      requestFeatureAccess(
        { id: item.featureId, name: item.label },
        () => { navigate(item.path); }
      );
      return;
    }
    
    if (onItemClick) {
      onItemClick(item);
    }
  };

  // Render based on variant
  if (variant === 'dock') {
    return (
      <div 
        className={`flex items-center gap-1 overflow-x-auto scrollbar-hide snap-x snap-mandatory ${className}`}
        style={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          WebkitOverflowScrolling: 'touch',
          maxWidth: '280px',
          margin: '0 auto',
          paddingLeft: '8px',
          paddingRight: '8px',
        }}
      >
        {navItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.path);
          const isItemLocked = item.isPro && !isPro && !(item.featureId && isFeatureUnlockedViaAd(item.featureId));
          
          return (
            <Tooltip key={item.id}>
              <TooltipTrigger asChild>
                <Link
                  to={isItemLocked ? '#' : item.path}
                  onClick={(e) => handleNavClick(e, item)}
                  data-testid={`dock-nav-${item.id}`}
                  className={`flex-shrink-0 flex h-12 w-12 items-center justify-center rounded-full transition-all duration-300 snap-center relative ${
                    active 
                      ? 'bg-primary text-primary-foreground shadow-lg scale-110' 
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                  }`}
                >
                  <Icon className={`h-6 w-6 ${active ? 'stroke-[2.5]' : ''}`} />
                  {isItemLocked && (
                    <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 flex items-center justify-center">
                      <Crown className="h-2.5 w-2.5 text-white" />
                    </span>
                  )}
                </Link>
              </TooltipTrigger>
              <TooltipContent side="top" className="text-xs">
                {item.label}
              </TooltipContent>
            </Tooltip>
          );
        })}
      </div>
    );
  }

  // Default: Sidebar variant
  return (
    <nav className={`flex flex-col gap-1 ${className}`} data-testid="global-menu-sidebar">
      {navItems.map((item) => {
        const Icon = item.icon;
        const active = isActive(item.path);
        const isItemLocked = item.isPro && !isPro && !(item.featureId && isFeatureUnlockedViaAd(item.featureId));

        return (
          <Tooltip key={item.id}>
            <TooltipTrigger asChild>
              <Link
                to={isItemLocked ? '#' : item.path}
                onClick={(e) => handleNavClick(e, item)}
                data-testid={`sidebar-nav-${item.id}`}
                className={`group flex items-center gap-3 rounded-xl px-4 py-3 transition-all duration-200 relative ${
                  active
                    ? 'bg-primary text-primary-foreground shadow-md'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                } ${item.isHighlight && !active ? 'ring-1 ring-amber-500/30' : ''}`}
              >
                <Icon className={`h-5 w-5 ${active ? 'stroke-[2]' : ''}`} />
                {showLabels && (
                  <span className="font-medium text-sm">{item.label}</span>
                )}
                {isItemLocked && (
                  <span className="ml-auto">
                    <Crown className="h-4 w-4 text-amber-500" />
                  </span>
                )}
                {item.isHighlight && !active && (
                  <Sparkles className="h-3 w-3 text-amber-500 ml-auto animate-pulse" />
                )}
              </Link>
            </TooltipTrigger>
            {!showLabels && (
              <TooltipContent side="right">
                {item.label}
              </TooltipContent>
            )}
          </Tooltip>
        );
      })}
    </nav>
  );
};

export default GlobalMenu;
